import "./styles/index.css";
import "./styles/profile.css";

import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Spin,
  Tabs,
  Input,
  Pagination,
  Button,
  DatePicker,
  Modal,
  Result,
} from "antd";
import { LoadingOutlined, TransactionOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { _toggle_restaurant_status } from "../../common/axios_services";

// components
import useUserProfile from "../../hooks/useUserProfile";
import { useRestaurantData } from "../../hooks/useRestaurantData";
import { useTransactionManagement } from "../../hooks/useTransactionManagement";
import { useOrderActions } from "../../hooks/useOrderActions";
import useDeliverySocket from "../../hooks/useDeliverySocket";
import OrderModals from "../../components/Restaurant/Dashboard/OrderModals";
import { OpenRestaurantModal } from "../../components/OpenResturant";
import RestaurantControls from "../../components/Restaurant/Dashboard/RestaurantControls";
import PendingApprovalModal from "../../components/PendingApprovalModal";
import DashboardHeader from "../../components/Restaurant/Dashboard/DashboardHeader";
import OrderManagement from "../../components/Restaurant/Dashboard/OrderManagement";
import FooterDisplay from "../../components/Restaurant/Footer";
import SupportManagerModal from "../../components/Restaurant/Dashboard/SupportManagerModal";
import ImpersonationBar from "./components/ImpersonationBar";
import HelpSupportButton from "../../components/HelpSupportButton";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../common/utils/notification";
import ImpersonationAccessDeniedModal from "../../components/ImpersonationAccessDeniedModal";
import RestaurantPendingModal from "../../components/modals/RestaurantPendingModal";
import PendingActivitiesBanner from "../../components/PendingActivitiesBanner";
import { ClockCircleOutlined } from "@ant-design/icons";

const BusinessDashboard = () => {
  const [restaurantPendingModalVisible, setRestaurantPendingModalVisible] = useState(false);
  const { TabPane } = Tabs;
  const newAntIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#008060" }} spin />
  );
  const navigate = useNavigate();

  // Hooks
  const { userProfile, refetchUserProfile } = useUserProfile();
  const {
    spinnerLoading,
    setSpinnerLoading,
    orderHist,
    orderMetrics,
    todaysOrders,
    processedPreviousOrders,
    supportManager,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    getSalesOrders,
    getTodaysDeliveryOrders,
    getPreviousOrders,
    getDeliveredOrders,
    getPendingOrders,
    getRestaurantOrderHist,
    getRestaurantOrderMetrics,
  } = useRestaurantData(userProfile, navigate);

  const transactionManagement = useTransactionManagement("restaurant");

  const handleConfirmPickup = async () => {
    refreshOrdersFunc();
  };

  const refreshOrdersFunc = () => {
    getRestaurantInfo();
    fetchSales();
    fetchTodaysOrders();
    fetchDeliveredOrders();
    fetchPendingOrders();
    getRestaurantOrderMetrics();
  };

  const {
    spinnerLoading: orderActionsLoading,
    orderColumns,
    isModalVisible,
    setIsModalVisible,
    isAcceptModalVisible,
    setIsAcceptModalVisible,
    isReadyModal,
    setIsReadyModal,
    selectedOrder,
    handleViewClick,
    handleAcceptOrder,
    handleRejectOrder,
    handleReadyOrder,
  } = useOrderActions(refreshOrdersFunc, navigate);

  // Local States
  const [today_page, setTodayPage] = useState(1);
  const [today_per_page, setTodayPerPage] = useState(10);
  const vendorRestaurant = userProfile?.business?.restaurants?.[0];
  const [activeTab, setActiveTab] = useState("todays_sales");

  const [restaurantOpenState, setRestaurantOpenState] = useState(() => {
    return vendorRestaurant?.isOpen ?? false;
  });
  const [isApproved, setIsApproved] = useState("");
  const [orderStatusFilter] = useState("all");
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [todaysOrdersData, setTodaysOrdersData] = useState({
    orders: [],
    pagination: {},
    summary: {},
  });
  const [salesData, setSalesData] = useState({
    orders: [],
    pagination: {},
    summary: {},
  });
  const [deliveredOrdersData, setDeliveredOrders] = useState({
    orders: [],
    pagination: {},
    summary: {},
  });
  const [pendingOrdersData, setPendingOrdersData] = useState({
    orders: [],
    pagination: {},
    summary: {},
  });

  const hasAutoOpenedRestaurantRef = useRef(false);
  const activeRestaurantActivitiesCount = useMemo(() => {
    return (Array.isArray(orderHist)
      ? orderHist
      : Array.isArray(orderHist?.orders)
        ? orderHist.orders
        : []
    ).filter((o) =>
      ["WAITING", "ACCEPTED", "READY", "IN_TRANSIT", "pending", "confirmed", "ready", "in_transit"].includes(
        o.status || o.delivery_status
      )
    ).length;
  }, [orderHist]);

  useEffect(() => {
    if (!hasAutoOpenedRestaurantRef.current && activeRestaurantActivitiesCount > 0) {
      setRestaurantPendingModalVisible(true);
      hasAutoOpenedRestaurantRef.current = true;
    }
  }, [activeRestaurantActivitiesCount]);

  const handleOrderStatusUpdate = (data) => {
    console.log("Order status updated:", data);
    fetchTodaysOrders();
    fetchPendingOrders();
    getRestaurantOrderMetrics();
    showSuccessNotification(`Order #${data.orderId} status: ${data.status}`);
  };

  const handleOrderAssigned = (data) => {
    console.log("Order assigned:", data);
    fetchTodaysOrders();
    fetchPendingOrders();

    showSuccessNotification(`Order #${data.orderId} assigned to biker`);
  };

  const handleBikerLocationUpdate = (data) => {
    console.log("Biker location update:", data);
  };

  const handleDeliveryPickedUp = (data) => {
    console.log("Delivery picked up:", data);
    fetchTodaysOrders();
    showSuccessNotification(`Order #${data.orderId} has been picked up`);
  };

  const handleDeliveryCompleted = (data) => {
    console.log("Delivery completed:", data);
    fetchTodaysOrders();
    fetchDeliveredOrders();
    showSuccessNotification(`Order #${data.orderId} has been delivered`);
  };

  const handleDeliveryDelayed = (data) => {
    console.log("Delivery delayed:", data);
    showErrorNotification(`Order #${data.orderId} is delayed`);
  };

  const {
    isConnected: deliverySocketConnected,
    connectionError: deliverySocketError,
  } = useDeliverySocket(
    handleOrderStatusUpdate,
    handleOrderAssigned,
    handleBikerLocationUpdate,
    null,
  );

  const change_today_pagination = (selected_page, pageSize) => {
    setTodayPage(selected_page);
    setTodayPerPage(pageSize);
  };

  const fetchTodaysOrders = async () => {
    const data = await getTodaysDeliveryOrders();
    setTodaysOrdersData(data);
  };

  const fetchSales = async () => {
    const data = await getSalesOrders();
    setSalesData(data);
  };

  const fetchDeliveredOrders = async () => {
    const data = await getDeliveredOrders();
    setDeliveredOrders(data);
  };

  const fetchPendingOrders = async () => {
    const data = await getPendingOrders();
    setPendingOrdersData(data);
  };

  useEffect(() => {
    if (vendorRestaurant && vendorRestaurant?.isOpen !== undefined) {
      setRestaurantOpenState(vendorRestaurant.isOpen);
      localStorage.setItem(
        "restaurantOpenState",
        JSON.stringify(vendorRestaurant.isOpen),
      );
    }
  }, [vendorRestaurant]);

  const getRestaurantInfo = () => {
    const business = userProfile?.business;
    setIsApproved(business?.status);

    if (
      business?.status === "pending" ||
      business?.status === "approved" ||
      business?.status === "rejected"
    ) {
      setShowApprovalModal(true);
    }
  };

  const handleSwitch = async (checked) => {
    if (!vendorRestaurant) return;

    try {
      setSpinnerLoading(true);
      const updateRestaurantProfile = await _toggle_restaurant_status(checked);
      showSuccessNotification(updateRestaurantProfile?.data?.message);
      refetchUserProfile();
      setRestaurantOpenState(checked);
      localStorage.setItem("restaurantOpenState", JSON.stringify(checked));
    } catch (err) {
      console.log({ err });
      showErrorNotification(err.response?.data?.message || err.message);
    } finally {
      setSpinnerLoading(false);
    }
  };

  useEffect(() => {
    refreshOrdersFunc();
  }, [userProfile?.business]);

  return (
    <>
      <div
        className="userprofile"
        style={
          isApproved === "pending"
            ? { pointerEvents: "none", opacity: 0.5 }
            : {}
        }
      >
        <Spin
          spinning={spinnerLoading || orderActionsLoading}
          indicator={newAntIcon}
        >
          <div className="profile-content pb-20">
            <div className="gray_bg_profile">
              <div className="profile_to_right">
                <div className="contain">
                  <div className="main_container">
                    <DashboardHeader orderMetrics={orderMetrics} />

                    <div className="flex justify-between items-center mb-4 p-2">
                      {!userProfile?.business?.isSpecial && (
                        <div
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-800 cursor-pointer transition-colors duration-200 rounded-lg hover:bg-blue-50"
                          onClick={() => navigate("/restaurant/transactions")}
                        >
                          <TransactionOutlined className="text-2xl" />
                          <span className="font-medium text-2xl">
                            View Transactions
                          </span>
                        </div>
                      )}

                      {supportManager && (
                        <div className="mb-4">
                          <SupportManagerModal
                            supportExecutive={supportManager.supportExecutive}
                            supportManager={supportManager.supportManager}
                          />
                        </div>
                      )}
                    </div>

                    {userProfile?.isRestaurantOwner && (
                      <RestaurantControls
                        restaurantOpenState={restaurantOpenState}
                        handleSwitch={handleSwitch}
                        userProfile={userProfile}
                        onTransferComplete={() => {
                          getRestaurantOrderMetrics();
                          transactionManagement.fetchUserWalletPayments();
                        }}
                        loading={spinnerLoading}
                      />
                    )}

                    <PendingActivitiesBanner
                      title="Pending Order Activities"
                      subtitle="Manage orders to accept, confirm ready, collection & in-transit"
                      buttonText="View Activities"
                      count={
                        (Array.isArray(orderHist)
                          ? orderHist
                          : Array.isArray(orderHist?.orders)
                            ? orderHist.orders
                            : []
                        ).filter((o) =>
                          ["WAITING", "ACCEPTED", "READY", "IN_TRANSIT", "pending", "confirmed", "ready", "in_transit"].includes(
                            o.status || o.delivery_status
                          )
                        ).length
                      }
                      onClick={() => setRestaurantPendingModalVisible(true)}
                    />

                    <Tabs
                      defaultActiveKey="todays_sales"
                      type="card"
                      activeKey={activeTab}
                      onChange={setActiveTab}
                    >
                      <TabPane tab={`SALES (${salesData.pagination.total || salesData.orders.length || 0})`} key="todays_sales">
                        <OrderManagement
                          todaysOrders={salesData.orders}
                          processedPreviousOrders={[]}
                          orderColumns={orderColumns}
                          today_page={today_page}
                          setActiveTab={setActiveTab}
                          activeTab={activeTab}
                          today_total={salesData.pagination.total || 0}
                          today_per_page={today_per_page}
                          userProfile={userProfile}
                          change_today_pagination={change_today_pagination}
                          spinnerLoading={orderActionsLoading}
                          newAntIcon={newAntIcon}
                          onViewOrder={handleViewClick}
                        />
                      </TabPane>
                      {/* <TabPane tab="TODAYS DELIVERY" key="todays_delivery">
                        <OrderManagement
                          todaysOrders={todaysOrdersData.orders}
                          processedPreviousOrders={[]}
                          orderColumns={orderColumns}
                          today_page={today_page}
                          setActiveTab={setActiveTab}
                          activeTab={activeTab}
                          today_total={todaysOrdersData.pagination.total || 0}
                          today_per_page={today_per_page}
                          userProfile={userProfile}
                          change_today_pagination={change_today_pagination}
                          spinnerLoading={orderActionsLoading}
                          newAntIcon={newAntIcon}
                          onViewOrder={handleViewClick}
                        />
                      </TabPane> */}
                      <TabPane tab={`SCHEDULED DELIVERY (${pendingOrdersData.pagination.total || pendingOrdersData.orders.length || 0})`} key="pending_delivery">
                        <OrderManagement
                          todaysOrders={pendingOrdersData.orders}
                          processedPreviousOrders={[]}
                          orderColumns={orderColumns}
                          today_page={today_page}
                          setActiveTab={setActiveTab}
                          activeTab={activeTab}
                          today_total={pendingOrdersData.pagination.total || 0}
                          today_per_page={today_per_page}
                          userProfile={userProfile}
                          change_today_pagination={change_today_pagination}
                          spinnerLoading={orderActionsLoading}
                          newAntIcon={newAntIcon}
                          onViewOrder={handleViewClick}
                        />
                      </TabPane>
                      {/* <TabPane tab="COMPLETED" key="completed">
                        <OrderManagement
                          todaysOrders={deliveredOrdersData.orders}
                          orderColumns={orderColumns}
                          today_page={today_page}
                          today_total={
                            deliveredOrdersData.pagination.total || 0
                          }
                          today_per_page={today_per_page}
                          userProfile={userProfile}
                          setActiveTab={setActiveTab}
                          change_today_pagination={change_today_pagination}
                          spinnerLoading={orderActionsLoading}
                          newAntIcon={newAntIcon}
                          onViewOrder={handleViewClick}
                        />
                      </TabPane> */}
                    </Tabs>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FooterDisplay />

          <OrderModals
            isModalVisible={isModalVisible}
            setIsModalVisible={setIsModalVisible}
            isAcceptModalVisible={isAcceptModalVisible}
            setIsAcceptModalVisible={setIsAcceptModalVisible}
            isReadyModal={isReadyModal}
            setIsReadyModal={setIsReadyModal}
            selectedOrder={selectedOrder}
            activeTab={activeTab}
            handleConfirmPickup={handleConfirmPickup}
            handleAcceptOrder={handleAcceptOrder}
            handleRejectOrder={handleRejectOrder}
            handleReadyOrder={handleReadyOrder}
          />

          <OpenRestaurantModal
            restaurantOpenState={restaurantOpenState}
            onOpen={handleSwitch}
          />

          <PendingApprovalModal
            visible={isApproved === "pending" || showApprovalModal}
            status={isApproved}
            onClose={() => {
              setShowApprovalModal(false);
              navigate("/");
            }}
            onContinue={() => navigate("/restaurant/menu")}
          />

          <HelpSupportButton page="RESTAURANT_DASHBOARD" businessId={userProfile?.business?.id} />

          <ImpersonationAccessDeniedModal
            allowedPaths={["/Restaurant/profile", "/Restaurant/menu"]}
          />

          <RestaurantPendingModal
            visible={restaurantPendingModalVisible}
            onClose={() => setRestaurantPendingModalVisible(false)}
            activeOrders={orderHist}
            onRefresh={() => {
              fetchTodaysOrders();
              fetchPendingOrders();
            }}
            onViewOrder={(order) => {
              setRestaurantPendingModalVisible(false);
              handleViewClick(order);
            }}
          />
        </Spin>
      </div>
    </>
  );
};

export default BusinessDashboard;
